import { css } from "@emotion/css";
import { DeprecatedSelect } from "@octopusdeploy/design-system-octopus-components";
import { space } from "@octopusdeploy/design-system-tokens";
import type { ArgoCDApplicationResource } from "@octopusdeploy/octopus-server-client";
import { ScriptingLanguage } from "@octopusdeploy/octopus-server-client";
import { noOp } from "@octopusdeploy/utilities";
import { useMemo, useState } from "react";
import type { Language } from "~/components/CodeEditor/CodeEditor";
import { CodeEditor, TextFormat } from "~/components/CodeEditor/CodeEditor";
import type { ConfiguredSources } from "../ConfiguredSource";
import type { ArgoCDAnnotationsFormat } from "../createAnnotation";
import { createCliAnnotationsPatchCommand, createAnnotation } from "../createAnnotation";
import { sharedStyles } from "../sharedStyles";
type AnnotationsViewProps = {
    index: number;
    configuredSources: ConfiguredSources;
    application?: ArgoCDApplicationResource;
};
const annotationFormats = [
    {
        value: "YAML",
        text: "YAML",
    },
    {
        value: "ARGOCD",
        text: "Argo CD CLI",
    },
    {
        value: "KUBECTL",
        text: "Kubectl",
    },
];
const styles = {
    contentContainer: css({
        display: "flex",
        flexDirection: "column",
        gap: space[16],
    }),
    selectContainer: css({
        width: "50%",
    }),
};
const supportedCliLanguages = [ScriptingLanguage.Bash, ScriptingLanguage.PowerShell];
const supportedYamlLanguages = [TextFormat.YAML];
export const AnnotationsView = (props: AnnotationsViewProps) => {
    const { index, configuredSources, application } = props;
    const [selectedAnnotationFormat, setSelectedAnnotationsFormat] = useState<ArgoCDAnnotationsFormat>("YAML");
    const [selectedSyntax, setSelectedSyntax] = useState<ScriptingLanguage | Language | TextFormat>(TextFormat.YAML);
    const annotation = useMemo(
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    () => (selectedAnnotationFormat === "YAML" ? createAnnotation(configuredSources) : createCliAnnotationsPatchCommand(selectedAnnotationFormat, selectedSyntax as ScriptingLanguage, configuredSources, application)), [application, configuredSources, selectedAnnotationFormat, selectedSyntax]);
    const onFormatChange = (val: string | undefined) => {
        // If swapping from CLI format to YAML we set the syntax to YAML
        // If swapping to a CLI format from yaml we default to Bash
        // If swapping to a CLI format to CLI we preserve the currently selected syntax e.g. Bash or Powershell
        if (val === "YAML") {
            setSelectedSyntax(TextFormat.YAML);
        }
        else if (selectedSyntax === TextFormat.YAML) {
            setSelectedSyntax(ScriptingLanguage.Bash);
        }
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        setSelectedAnnotationsFormat(val as ArgoCDAnnotationsFormat);
    };
    const annotationLineCount = annotation.split(/\r\n|\r|\n/).length;
    //adjust the height of the code editor based on the number of lines in the annotation
    const codeEditorContainer = css({
        // 1.25rem per line (line height) and roughly 3rem of padding/margins/scrollbars
        minHeight: `calc((${annotationLineCount} * 1.25rem) + 3rem)`,
    });
    return (<div>
            <h2 className={sharedStyles.header}>{index}. Copy annotations</h2>
            <p>Add the annotations below to your Argo CD application</p>{" "}
            <div className={styles.contentContainer}>
                <div className={styles.selectContainer}>
                    <DeprecatedSelect items={annotationFormats} value={selectedAnnotationFormat} onChange={onFormatChange} label={"Format"} allowClear={false} allowFilter={false}/>
                </div>
            </div>
            <CodeEditor value={annotation} readOnly lineWrapping={false} showCopyButton showLineNumbers language={selectedSyntax} allowFullScreen={false} containerClassName={codeEditorContainer} settingsOverride={{ theme: "default", wordWrap: false }} onChange={noOp} scriptingLanguageSelectorOptions={{
            supportedLanguages: selectedAnnotationFormat === "YAML" ? supportedYamlLanguages : supportedCliLanguages,
            onScriptingLanguageChanged: (syntax: ScriptingLanguage | Language | TextFormat) => setSelectedSyntax(syntax),
        }}/>
        </div>);
};
